import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '@cbuild/cs-common-data-services';
import { Feature, Module, PermissionComponent } from '@cbuild/shared';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { OrganizationConfigurationEnum } from '../../../shared/enums/organization-configurations-enum';
import { UserCompanyInfo } from '../../models/user-company-info.model';
import { UserInfo } from '../../models/user-info.model';
import { ApplicationService } from '../../services/application.service';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { PropertyFilter } from '../../services/property-filter';
import { NavMenuService } from '../nav-menu.service';

/**
 * @dynamic
 */
@Component({
  selector: 'cs-nav-profile-dropdown',
  standalone: false,
  templateUrl: './nav-profile-dropdown.component.html',
  styleUrls: ['./nav-profile-dropdown.component.scss'],
  animations: [
    trigger('navDropDown', [
      transition(':enter', [
        style({ opacity: 0, right: '-30px' }),
        animate('200ms ease-in-out', style({ opacity: 1, right: '*' }))
      ]),
      transition(':leave', [
        style({ opacity: 1, right: '*' }),
        animate('100ms ease-in-out', style({ opacity: 0, right: '-30px' }))
      ])
    ])
  ]
})
export class NavProfileDropdownComponent extends PermissionComponent
  implements OnDestroy {
  private _onDestroy$ = new Subject();
  public propertyLogo: string;
  public propertyName: string;
  public companyName: string;

  @Input() public userInfo: UserInfo;
  @Output()
  public propertyFilter = new EventEmitter<PropertyFilter>();
  @HostBinding('class.cs-nav-drop-down-opened')
  public displayUserDropDown = false;
  public opened: number | undefined = 1;
  public module = Module;
  public feature = Feature;
  private appliedPropertyFilter: PropertyFilter;
  public orgConfigEnum = OrganizationConfigurationEnum;

  constructor(
    authenticationService: AuthenticationService,
    private storageService: StorageService,
    private router: Router,
    private applicationService: ApplicationService,
    private navMenuService: NavMenuService
  ) {
    super(authenticationService);
    const { $propertyFilter, $hasProjects } = this.applicationService;
    combineLatest([$propertyFilter, $hasProjects])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([_]) => {
        this.appliedPropertyFilter = _;
        this.updateCoreView(_);
      });

    this.navMenuService.$openedDropDown
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(val => {
        if (this !== val) {
          this.closeDropDown();
        }
      });
  }

  public openPanel(i: number, event: Event): void {
    this.opened = this.opened !== i ? i : undefined;
    event.preventDefault();
    event.stopPropagation();
  }

  public logout(): void {
    this.authenticationService.logOut();
    this.storageService.removeItem('property');
    this.storageService.removeItem('page_options');
    this.storageService.removeItem('lead_filters');
    this.storageService.removeItem('payment_history_filters');
    this.storageService.removeItem('customer_filters');
    this.storageService.removeItem('campaign_leads_filters');
  }

  public clearUserData(): void {
    this.authenticationService.logOut();
    this.storageService.removeItem('property');
    this.storageService.removeItem('page_options');
    this.storageService.removeItem('lead_filters');
    this.storageService.removeItem('payment_history_filters');
    this.storageService.removeItem('customer_filters');
    this.storageService.removeItem('campaign_leads_filters');
  }

  public async selectProperty(): Promise<void> {
    await this.router.navigate([], {
      queryParams: { property: undefined },
      queryParamsHandling: 'merge'
    });
  }

  public async applyPropertyFilter(
    propertyId: number | null,
    companyId: number | null,
    propertyTypeId: number | null
  ): Promise<void> {
    await this.resetRoute();
    this.emitProjectEvent({
      propertyId,
      companyId,
      propertyTypeId
    });
  }

  public openDropDown(): void {
    this.navMenuService.$openedDropDown.next(this);
    this.displayUserDropDown = true;
  }

  public closeDropDown(): void {
    this.displayUserDropDown = false;
  }

  public ngOnDestroy(): void {
    this.closeDropDown();
  }

  public async resetRoute(): Promise<void> {
    await this.router.navigate([], {
      queryParams: { property: undefined },
      queryParamsHandling: 'merge'
    });
  }

  public resetFilters(): void {
    this.storageService.setItem('lead_filters', null);
  }

  public emitProjectEvent(propertyFilter: PropertyFilter): void {
    this.applicationService.switchedFromMenu.next(0);
    this.propertyFilter.emit(propertyFilter);
  }

  public get selectedCompany(): number | null {
    return this.appliedPropertyFilter?.companyId;
  }

  public get selectedProject(): number | null {
    return this.appliedPropertyFilter?.propertyId;
  }

  public getImage(i: number): string {
    switch (i % 4) {
      case 0:
        return '/assets/images/project-1.png';
      case 1:
        return '/assets/images/project-2.png';
      case 2:
        return '/assets/images/project-3.png';
      case 3:
        return '/assets/images/project-4.png';
      default:
        return '/assets/images/project-1.png';
    }
  }

  public get companiesList(): Array<UserCompanyInfo> {
    return this.applicationService.companiesList || [];
  }

  private updateCoreView(_: PropertyFilter): void {
    const project = this.applicationService.getProject(_.propertyId);
    this.propertyName = project ? project.propertyName : '';
    this.propertyLogo = project ? project.propertyLogo : '';
    const company = this.applicationService.getCompany(_.companyId);
    this.companyName = company ? company.companyName : '';
  }
}
